import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Printer } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { api, fmtErr } from "../../lib/api";
import AddBookingDialog from "./AddBookingDialog";

function todayLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const STATUS_STYLES = {
  confirmed: "bg-emerald-400/10 text-emerald-300 border-emerald-400/30",
  seated: "bg-blue-400/10 text-blue-300 border-blue-400/30",
  completed: "bg-zinc-400/10 text-zinc-300 border-zinc-400/30",
  cancelled: "bg-red-400/10 text-red-300 border-red-400/30",
  no_show: "bg-amber-400/10 text-amber-300 border-amber-400/30",
  pending: "bg-orange-400/10 text-orange-300 border-orange-400/30",
};

export default function BookingsTab({ restaurant }) {
  const [date, setDate] = useState(todayLocal());
  const [bookings, setBookings] = useState([]);
  const [tables, setTables] = useState([]);
  const [q, setQ] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const saveEdit = async () => {
    try {
      await api.patch(`/restaurants/${restaurant.id}/bookings/${editing.id}`, {
        party_size: Number(editing.party_size),
        phone: editing.phone || "",
        occasion: editing.occasion || "",
        notes: editing.notes || "",
      });
      toast.success("Booking updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const load = () => {
    const params = {};
    if (date) params.date = date;
    if (q) params.q = q;
    api.get(`/restaurants/${restaurant.id}/bookings`, { params }).then((r) => setBookings(r.data)).catch(() => {});
  };

  useEffect(() => { load(); }, [date, q, restaurant.id]);
  useEffect(() => {
    api.get(`/restaurants/${restaurant.id}/tables`).then((r) => setTables(r.data)).catch(() => {});
  }, [restaurant.id]);

  const patch = async (bid, body, msg) => {
    try {
      await api.patch(`/restaurants/${restaurant.id}/bookings/${bid}`, body);
      if (msg) toast.success(msg);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  return (
    <div data-testid="bookings-tab">
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <Input data-testid="bookings-date-filter" type="date" value={date} onChange={(e) => setDate(e.target.value)}
          className="bg-card border-border/60 max-w-[200px]" />
        <button data-testid="bookings-clear-date" onClick={() => setDate("")}
          className="text-sm text-muted-foreground hover:text-foreground transition-colors">
          Show all
        </button>
        <Input data-testid="bookings-search-input" placeholder="Search name, email or table..." value={q}
          onChange={(e) => setQ(e.target.value)} className="bg-card border-border/60 max-w-[260px]" />
        <Button data-testid="print-guest-list-btn" size="sm" variant="outline" className="rounded-full ml-auto"
          onClick={() => window.open(`/print/guest-list/${restaurant.id}/${date || todayLocal()}`, "_blank")}>
          <Printer className="w-4 h-4 mr-2" /> Guest list
        </Button>
        <Button data-testid="add-booking-btn" size="sm" className="rounded-full" onClick={() => setAddOpen(true)}>
          <Plus className="w-4 h-4 mr-2" /> Add booking
        </Button>
      </div>
      <div className="rounded-2xl border border-border/60 bg-card overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-border/60">
              <TableHead>Guest</TableHead>
              <TableHead>When</TableHead>
              <TableHead>Party</TableHead>
              <TableHead>Table</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {bookings.map((b) => (
              <TableRow key={b.id} data-testid={`booking-row-${b.id}`} className="border-border/40">
                <TableCell>
                  <p className="font-medium">{b.name}</p>
                  <p className="text-xs text-muted-foreground">{b.email}</p>
                  {b.dietary && <p className="text-xs text-amber-500 mt-0.5">Dietary: {b.dietary}</p>}
                  {b.occasion && <p data-testid={`occasion-${b.id}`} className="text-xs text-primary mt-0.5">Occasion: {b.occasion}</p>}
                  {b.source && b.source !== "web" && (
                    <span data-testid={`source-${b.id}`} className="inline-block mt-1 text-[10px] px-2 py-0.5 rounded-full border border-border/60 text-muted-foreground capitalize">{b.source}</span>
                  )}
                  {b.preorder && (
                    <p data-testid={`preorder-${b.id}`} className="text-xs text-primary mt-0.5">
                      Pre-order: {b.preorder.name} × {b.preorder.qty} (£{Number(b.preorder.total).toFixed(2)}){b.payment_status === "paid" ? " — paid" : ""}
                    </p>
                  )}
                  {(b.answers || []).map((a) => (
                    <p key={a.id} data-testid={`answer-${b.id}-${a.id}`} className="text-xs text-muted-foreground mt-0.5">{a.label}: {a.answer}</p>
                  ))}
                  {b.risk && b.risk.level !== "none" && (
                    <span data-testid={`risk-${b.id}`} title={b.risk.reasons.join(" · ")}
                      className={`inline-block mt-1 text-[10px] px-2 py-0.5 rounded-full border ${b.risk.level === "high" ? "text-red-400 border-red-400/40 bg-red-400/10" : b.risk.level === "medium" ? "text-amber-400 border-amber-400/40 bg-amber-400/10" : "text-muted-foreground border-border/60"}`}>
                      {b.risk.level === "high" ? "No-show risk: high" : b.risk.level === "medium" ? "No-show risk: medium" : "No-show risk: low"}
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-sm">{b.date} · {b.time}</TableCell>
                <TableCell>{b.party_size}</TableCell>
                <TableCell>
                  {(b.table_ids || []).length > 1 ? (
                    <span data-testid={`combo-label-${b.id}`} className="text-xs font-medium text-primary">{b.table_label} (combined)</span>
                  ) : (
                  <Select value={b.table_id || ""} onValueChange={(v) => patch(b.id, { table_id: v }, "Table reassigned")}>
                    <SelectTrigger data-testid={`assign-table-${b.id}`} className="w-28 h-8 bg-background border-border/60 text-xs">
                      <SelectValue placeholder="Assign" />
                    </SelectTrigger>
                    <SelectContent>
                      {tables.filter((t) => t.active).map((t) => (
                        <SelectItem key={t.id} value={t.id}>{t.label} ({t.capacity})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className={STATUS_STYLES[b.status]}>{b.status.replace("_", " ")}</Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1.5">
                    <Button data-testid={`edit-booking-${b.id}`} size="sm" variant="ghost" className="h-8 w-8 p-0 rounded-full"
                      onClick={() => setEditing({ id: b.id, name: b.name, party_size: b.party_size, phone: b.phone || "", occasion: b.occasion || "", notes: b.notes || "" })}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Select value="" onValueChange={(v) => patch(b.id, { status: v }, "Status updated")}>
                    <SelectTrigger data-testid={`status-select-${b.id}`} className="w-32 h-8 bg-background border-border/60 text-xs">
                      <SelectValue placeholder="Update" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="seated">Seated</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="no_show">No show</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {bookings.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground" data-testid="no-bookings-msg">
                No bookings for this filter.
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <AddBookingDialog restaurant={restaurant} tables={tables} open={addOpen} onOpenChange={setAddOpen} onAdded={load} />

      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent className="bg-card border-border/60">
          {editing && (
            <>
              <DialogHeader><DialogTitle className="font-outfit">Edit booking — {editing.name}</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-2" data-testid="edit-booking-form">
                <div>
                  <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Party size</Label>
                  <Input data-testid="edit-party-input" type="number" min="1" value={editing.party_size}
                    onChange={(e) => setEditing({ ...editing, party_size: e.target.value })} className="mt-1.5 bg-background border-border/60" />
                </div>
                <div>
                  <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Phone</Label>
                  <Input data-testid="edit-phone-input" placeholder="Phone number" value={editing.phone}
                    onChange={(e) => setEditing({ ...editing, phone: e.target.value })} className="mt-1.5 bg-background border-border/60" />
                </div>
                <div>
                  <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Occasion</Label>
                  <Input data-testid="edit-occasion-input" placeholder="e.g. Birthday, Anniversary" value={editing.occasion}
                    onChange={(e) => setEditing({ ...editing, occasion: e.target.value })} className="mt-1.5 bg-background border-border/60" />
                </div>
                <div>
                  <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Notes</Label>
                  <Textarea data-testid="edit-notes-input" rows={2} placeholder="Special requests" value={editing.notes}
                    onChange={(e) => setEditing({ ...editing, notes: e.target.value })} className="mt-1.5 bg-background border-border/60" />
                </div>
                <Button data-testid="save-edit-booking-btn" onClick={saveEdit} className="w-full rounded-full">Save changes</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
